import type { ScenarioContent, StepSchema } from 'shared';
import { resolveMinigameEntry } from '../config/minigameRegistry';
import { minigameComponents, minigameComponentsByTemplateId } from '../minigames/templates';
import { resolveMediaRefToUrl } from './mediaRef';

export interface PreloadProgress {
  total: number;
  loaded: number;
  currentUrl?: string;
  failed: { url: string; message: string }[];
}

const PRELOAD_CACHE_NAME = 'scenario-preload-v1';

export function collectScenarioPreloadUrls(scenario: ScenarioContent): string[] {
  const urls = new Set<string>();

  const visit = (value: unknown) => {
    if (typeof value === 'string') {
      const resolved = resolveMediaRefToUrl(value) ?? (isHttpUrl(value) ? value : null);
      if (resolved) {
        urls.add(resolved);
      }
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (value && typeof value === 'object') {
      Object.values(value as Record<string, unknown>).forEach(visit);
    }
  };

  visit(scenario.startScreen);
  visit(scenario.meta);
  visit(scenario.clues);
  visit(scenario.routeMap);
  for (const node of scenario.nodes ?? []) {
    visit(node.meta);
    for (const step of node.steps ?? []) {
      visit(step);
      if (step.type === 'minigame') {
        const minigameStep = step as Extract<StepSchema, { type: 'minigame' }>;
        const hasReactTemplate =
          Boolean(minigameStep.registryId && minigameComponents[minigameStep.registryId]) ||
          Boolean(minigameStep.templateId && minigameComponentsByTemplateId[minigameStep.templateId]);
        if (hasReactTemplate) {
          continue;
        }
        const entry = resolveMinigameEntry(minigameStep);
        if (entry?.entryUrl) {
          urls.add(entry.entryUrl);
        }
      }
    }
  }

  return Array.from(urls);
}

export async function preloadScenarioAssets(
  scenario: ScenarioContent,
  onProgress?: (progress: PreloadProgress) => void
): Promise<PreloadProgress> {
  const urls = collectScenarioPreloadUrls(scenario);
  const progress: PreloadProgress = {
    total: urls.length,
    loaded: 0,
    failed: []
  };
  onProgress?.(progress);

  const cache = await openPreloadCache();
  for (const url of urls) {
    progress.currentUrl = url;
    onProgress?.({ ...progress, failed: [...progress.failed] });
    try {
      if (cache) {
        const cached = await cache.match(url);
        if (cached) {
          progress.loaded += 1;
          onProgress?.({ ...progress, failed: [...progress.failed] });
          continue;
        }
      }

      const response = await fetch(url, { cache: 'force-cache' });
      if (!response.ok && response.type !== 'opaque') {
        throw new Error(`HTTP ${response.status}`);
      }
      if (cache) {
        await cache.put(url, response.clone());
      }
    } catch (error) {
      progress.failed.push({
        url,
        message: error instanceof Error ? error.message : String(error)
      });
    } finally {
      progress.loaded += 1;
      onProgress?.({ ...progress, failed: [...progress.failed] });
    }
  }

  return { ...progress, currentUrl: undefined, failed: [...progress.failed] };
}

async function openPreloadCache(): Promise<Cache | null> {
  if (typeof window === 'undefined' || !('caches' in window)) {
    return null;
  }
  try {
    return await window.caches.open(PRELOAD_CACHE_NAME);
  } catch {
    return null;
  }
}

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}
